/**
 * Autocorrect / override system.
 *
 * Append ! to any value to suppress autocorrection for that field.
 * The ! is STICKY: it stays in the stored value (state, localStorage and the
 * saved project file) so a later blur or project load never silently
 * re-applies the soft rule. lib/api toPayload strips it before sending.
 *
 *   "75"   on blur → "76"    (autocorrected to nearest standard size)
 *   "75!"  on blur → "75!"   (override — value kept, marker kept)
 *   "!"    on blur → ""      (marker alone means nothing)
 *
 * Delete the ! to drop the override; the soft rule then applies on next blur.
 *
 * The ! only bypasses the SOFT range. Hard limits (lib/limits) still apply and
 * are reported as errors by lib/validators.
 *
 * Adding autocorrect to a new field:
 *   1. Give the field a `soft` range in lib/limits, then add
 *      `field: softRule("field")` to AUTOCORRECT_RULES below.
 *   2. Add  onBlur={blur("fieldName")}  to that CalcFieldRow in Calculator.tsx.
 */
import type { CalcInputs } from "@/types/calc";
import { CALC_LIMITS } from "@/lib/limits";

// ── Helpers ──────────────────────────────────────────────────────────────────

/** True when the user appended ! to signal "keep this value as-is". */
export function hasOverride(v: string): boolean {
  return String(v ?? "").includes("!");
}

/** Remove the override marker and trim whitespace. */
export function stripOverride(v: string): string {
  return String(v ?? "").replace(/!/g, "").trim();
}

/** Normalised overridden value: the cleaned number plus one trailing "!". */
export function withOverride(v: string): string {
  const clean = stripOverride(v);
  return clean === "" ? "" : `${clean}!`;
}

/** Clamp n to [min, max], then snap to the nearest multiple of step. */
function snapToStep(n: number, step: number, min: number, max: number): number {
  const clamped = Math.max(min, Math.min(max, n));
  const snapped = Math.round(clamped / step) * step;
  // Re-clamp after rounding (guards the rare case where min/max aren't
  // exact multiples of step — not currently the case but safe to keep).
  return Math.max(min, Math.min(max, snapped));
}

/**
 * Return the value in `sorted` (ascending) that is closest to `n`.
 * Ties go to the LOWER value: reduce only replaces `best` on a strictly
 * smaller distance, so the earlier (lower) candidate wins an equal one.
 */
function nearestInSet(n: number, sorted: readonly number[]): number {
  return sorted.reduce((best, v) =>
    Math.abs(v - n) < Math.abs(best - n) ? v : best,
  );
}

// ── Rule type ─────────────────────────────────────────────────────────────────

/**
 * Receives the *cleaned* value string (! already stripped).
 * Returns the corrected string, or null if the value is already valid.
 */
export type AutocorrectRule = (cleaned: string) => string | null;

/**
 * Build the soft-range rule for a field from its lib/limits entry:
 *   - snap set  → clamp to soft min/max, then nearest value in the set
 *   - step      → clamp, then snap to the nearest multiple of step
 *   - otherwise → clamp (integer fields are rounded first)
 * Hard-range violations are NOT corrected here — lib/validators reports them.
 */
function softRule(key: keyof CalcInputs): AutocorrectRule {
  const limit = CALC_LIMITS[key];
  const soft = limit?.soft;
  return (v) => {
    if (!soft) return null;
    const n = Number(v);
    if (v.trim() === "" || Number.isNaN(n)) return null;
    let result: number;
    if (soft.snap) {
      result = nearestInSet(Math.max(soft.min, Math.min(soft.max, n)), soft.snap);
    } else if (soft.step) {
      result = snapToStep(n, soft.step, soft.min, soft.max);
    } else {
      const base = limit!.integer ? Math.round(n) : n;
      result = Math.max(soft.min, Math.min(soft.max, base));
    }
    return String(result) === v.trim() ? null : String(result);
  };
}

// ── Rules registry ────────────────────────────────────────────────────────────
//
// Ranges live in lib/limits (CALC_LIMITS[key].soft) — edit them there.

export const AUTOCORRECT_RULES: Partial<Record<keyof CalcInputs, AutocorrectRule>> = {

  // ── Product Properties ────────────────────────────────────────────────────

  /** Inlet Temperature — 2 to 35 °C. */
  inletTemp: softRule("inletTemp"),

  /** Outlet Temperature — −23 to −10 °C. */
  outletTemp: softRule("outletTemp"),

  /** Density — 700 to 1200 kg/m³. */
  density: softRule("density"),

  /** Contact % — 50 to 100 %. */
  contactPc: softRule("contactPc"),

  // ── Refrigerant ───────────────────────────────────────────────────────────

  /** Evaporation Temperature — −50 to −30 °C. */
  evapTemp: softRule("evapTemp"),

  /** Fluid ΔT (Secondary) — 2 to 6 K. */
  htfDeltaT: softRule("htfDeltaT"),

  // ── Freezer Sizing ────────────────────────────────────────────────────────

  /**
   * Station Length — 750–1260 mm in 10 mm steps.
   */
  lStation: softRule("lStation"),

  /**
   * Station Depth — 400–800 mm in 10 mm steps.
   */
  dStation: softRule("dStation"),

  /**
   * Station Thickness — snaps to the nearest standard plate spacing in the
   * range 50–120 mm (T_STATION_SNAP in lib/limits).  Valid values are 5 mm
   * multiples, with 76 mm and 102 mm substituted for 75 and 100 respectively
   * to preserve the physical spacer corrections (75→76, 100/101→102).
   *
   * Valid set: 50, 55, 60, 65, 70, 76, 80, 85, 90, 95, 102, 105, 110, 115, 120 mm.
   * Ties snap DOWN to the lower value.
   * Examples:
   *   54  → 55   (nearest 5 mm step)
   *   75  → 76   (76 replaces 75; |75-76|=1 beats |75-70|=5)
   *   78  → 76   (|78-76|=2 ties |78-80|=2 — tie → lower wins)
   *   100 → 102  (102 replaces 100; |100-102|=2 beats |100-95|=5)
   *   101 → 102  (nearest)
   *   103 → 102  (nearest)
   *   104 → 105  (|104-105|=1 beats |104-102|=2)
   */
  tStation: softRule("tStation"),

  /**
   * Number of Stations — integer clamped to 6–48.
   */
  nStations: softRule("nStations"),

  // ── Cycle times ───────────────────────────────────────────────────────────

  /** Loading Time — 5 to 60 s/stn. */
  loadingTime: softRule("loadingTime"),

  /** Unloading Time — 5 to 60 s/stn. */
  unloadingTime: softRule("unloadingTime"),
};

// ── On-blur handler ───────────────────────────────────────────────────────────

/**
 * Call this when a field loses focus.
 *
 * - Value contains !  → keep the numeric part unchanged, normalised to "n!"
 *                       (the override persists — see the header comment).
 * - No !              → apply the autocorrect rule for that field (if any).
 *
 * Returns the new string to store in state. See applyOnBlurWithNote for the
 * variant that also explains a correction to the user.
 */
export function applyOnBlur(key: keyof CalcInputs, value: string): string {
  const override = hasOverride(value);
  const clean = stripOverride(value);

  if (override) {
    return withOverride(clean); // user opted out of autocorrect — keep the flag
  }

  const rule = AUTOCORRECT_RULES[key];
  if (rule) {
    const corrected = rule(clean);
    return corrected !== null ? corrected : clean;
  }
  return clean;
}

// ── Blur with feedback ────────────────────────────────────────────────────────

/** Result of a blur: the value to store, plus a short note when the value was
 *  changed by the soft rule (shown under the field until the next edit). */
export interface BlurResult {
  value: string;
  note?: string;
}

/**
 * Why a soft rule changed `from` into `to`, e.g.
 *   "40 → 35 (max). Add ! to keep 40."
 *   "815 → 820 (standard size)"
 */
function correctionNote(key: keyof CalcInputs, from: string, to: string): string {
  const soft = CALC_LIMITS[key]?.soft;
  const n = Number(from);
  if (soft && n > soft.max) return `${from} → ${to} (max). Add ! to keep ${from}.`;
  if (soft && n < soft.min) return `${from} → ${to} (min). Add ! to keep ${from}.`;
  if (soft && (soft.snap || soft.step)) return `${from} → ${to} (standard size)`;
  return `${from} → ${to} (whole number)`;
}

/** applyOnBlur, plus a note when the soft rule changed the value. */
export function applyOnBlurWithNote(key: keyof CalcInputs, value: string): BlurResult {
  const next = applyOnBlur(key, value);
  if (hasOverride(value)) return { value: next };
  const clean = stripOverride(value);
  if (clean === "" || next === clean) return { value: next };
  return { value: next, note: correctionNote(key, clean, next) };
}
